// The working panel's model, folded from `journal` frames (deep-research bridge/journal.py).
//
// One fold serves both feeds: the live channel streams frames as the run works and the
// finished journal re-sends the whole sequence. Frames are idempotent — header merges,
// plan replaces, blocks upsert by id — so replaying the finished journal over live state
// UPGRADES the panel in place: blocks keep their identity, only the header pill, the plan
// states and the footer change. Live blocks the finished journal did not restate are
// pruned when its footer (final: true) lands.
//
// STEPS ARE IN THE RECORD, NOT IN THE VIEW. The frames still carry a plan, each block's
// `step_n` and the audit's verdicts — an engineer reads them in the run's own text. The
// panel shows none of it: a state word ("Adapted", "Not reached") is a judgement a reader
// cannot check, and one live run graded 8 of 8 steps "adapted" over work plainly done.
// What the panel shows is the work, in the order it happened.

export const JOURNAL_VERSION = 4;

export function isJournalMessage(message) {
  return !!message && message.type === "journal" && !!message.data && typeof message.data.frame === "string";
}

export function isJournal(messages) {
  return Array.isArray(messages) && messages.length > 0 && isJournalMessage(messages[0]);
}

export const emptyJournal = () => ({
  version: JOURNAL_VERSION,
  run: null,          // header.run, shallow-merged
  plan: [],           // plan.steps, replaced whole
  attribution: "none", // plan.attribution: declared | credited | none
  byId: {},           // block id → block
  order: [],          // block ids in arrival order
  footer: null,       // footer frame
  error: "",          // error frame text
  final: false,       // a finished journal's header has landed
  restated: null,     // ids re-sent since the final header (for pruning)
});

export function foldFrame(model, data) {
  if (!data || typeof data !== "object") return model;
  if (typeof data.v === "number" && data.v > JOURNAL_VERSION) return model; // forward compat: render nothing new
  switch (data.frame) {
    case "header": {
      const run = { ...(model.run || {}), ...(data.run || {}) };
      if (data.run && data.run.verification) {
        run.verification = { ...((model.run || {}).verification || {}), ...data.run.verification };
      }
      const final = !!data.final || model.final;
      return { ...model, run, final, restated: data.final ? new Set() : model.restated };
    }
    case "plan":
      return {
        ...model,
        plan: Array.isArray(data.steps) ? data.steps : model.plan,
        attribution: typeof data.attribution === "string" ? data.attribution : model.attribution,
      };
    case "block": {
      if (!data.id || !data.kind) return model;
      const seen = Object.prototype.hasOwnProperty.call(model.byId, data.id);
      const byId = { ...model.byId, [data.id]: data };
      const order = seen ? model.order : [...model.order, data.id];
      const restated = model.restated ? new Set(model.restated).add(data.id) : null;
      return { ...model, byId, order, restated };
    }
    case "footer": {
      let byId = model.byId;
      let order = model.order;
      if (data.final && model.restated) {
        order = model.order.filter((id) => model.restated.has(id));
        byId = {};
        order.forEach((id) => { byId[id] = model.byId[id]; });
      }
      return { ...model, footer: data, byId, order, restated: null };
    }
    case "error":
      return { ...model, error: data.text || "The analysis could not finish." };
    default:
      return model;
  }
}

export function journalFromMessages(messages) {
  let model = emptyJournal();
  (messages || []).forEach((message) => {
    if (isJournalMessage(message)) model = foldFrame(model, message.data);
  });
  return model;
}

// ── selectors ───────────────────────────────────────────────────────────────

export function blocksOf(model) {
  return model.order.map((id) => model.byId[id]).filter(Boolean);
}

// Within a step: an adapted/skipped line first, the run's blocks in event order (by the
// trace's seq, else arrival), a failed/not-reached line after them, the finding last —
// whichever feed they came from and in whatever order they arrived.
function rankOf(block, arrival) {
  if (typeof block.seq === "number") return block.seq;
  return 2e15 + arrival;
}



// THE RUN, IN ORDER: every block as it arrived, whatever step it belongs to. This is the
// panel's only sequence — live, and after the answer lands. The chain is left out because
// it is drawn above the feed as the picture of the finding.
//
// Ordering: the trace's own `seq` where a block has one, arrival otherwise. A step's
// finding and deviation lines are stamped when the step closes, so they sort by arrival
// and land after the work they summarise.
export function feedBlocks(model) {
  const arrival = (b) => model.order.indexOf(b.id);
  return blocksOf(model)
    .filter((b) => b.kind !== "chain")
    .sort((a, b) => rankOf(a, arrival(a)) - rankOf(b, arrival(b)) || arrival(a) - arrival(b));
}

// What the run is doing this second: the action still running, else the reported stage.
export function currentActivity(model) {
  const running = feedBlocks(model).filter((b) => b.kind === "action" && b.state === "running");
  if (running.length) {
    const b = running[running.length - 1];
    return `${b.label} ${b.detail}`.trim();
  }
  return (model.run && model.run.stage) || "";
}


export function chainOf(model) {
  return blocksOf(model).find((b) => b.kind === "chain") || null;
}

export function visibleSteps(model) {
  return (model.plan || []).filter((s) => !s.hidden);
}

export function isLive(model) {
  const status = model.run && model.run.status;
  return !model.final && (status === "running" || status === "queued" || !status);
}



// ── words ───────────────────────────────────────────────────────────────────

export function formatDuration(ms) {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) return "";
  const seconds = Math.max(0, Number(ms) / 1000);
  if (seconds < 10) return `${seconds.toFixed(1).replace(/\.0$/, "")}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds - minutes * 60);
  return `${minutes}m ${rest}s`;
}

// ── the verdict's words ───────────────────────────────────────────────────────
//
// THE BACKEND NAMES THE VERDICT; nothing here infers one. Until 2026-10-17 every answer
// whose figure checks passed was `status: "answered"`, and this read that as "Verified" —
// on the 2026-10-16 golden baseline 32 of 59 such answers were wrong. The header now
// carries `verification.label` (and `label_text`, the backend's own wording); "Verified"
// is said only when that label says so. Headers from older backends carry only `verdict`
// and are read as before, except that an unknown verdict is never promoted to Verified.
const LABELS = {
  verified:       { key: "ok",     label: "Verified" },
  fit_checked:    { key: "ok",     label: "Figures and fit checked" },
  figures_traced: { key: "warn",   label: "Figures checked, fit not confirmed" },
  unverified:     { key: "warn",   label: "Not verified" },
  partial:        { key: "warn",   label: "Partial" },
  declined:       { key: "warn",   label: "Could not answer reliably" },
  asked:          { key: "grey",   label: "Asked a question back" },
  failed:         { key: "failed", label: "Failed" },
};

// The pill for a backend label, in the backend's own words when it sends them; null for
// a label this version does not know, or none at all.
export function labelOf(label, text) {
  const known = LABELS[label];
  if (!known) return null;
  return { key: known.key, label: String(text || "").trim() || known.label };
}

export function verdictOf(model) {
  const run = model.run || {};
  const ver = run.verification || {};
  if (model.error) return { key: "failed", label: "Failed" };
  if (run.status === "queued") return { key: "running", label: "Starting" };
  if (isLive(model)) return { key: "running", label: "Running" };
  // An explicit label beats the "no analysis" inference from an empty record: a run that
  // asked a question back has no blocks either, and "No analysis" would hide why.
  const labelled = labelOf(ver.label, ver.label_text);
  if (labelled) return labelled;
  if (ver.verdict === "none" || (!model.plan.length && !model.order.length)) {
    return { key: "grey", label: "No analysis" };
  }
  switch (ver.verdict) {
    case "verified": return { key: "ok", label: "Verified" };
    case "verified_adapted": return { key: "ok", label: "Verified · method adapted" };
    case "partial": return { key: "warn", label: "Partial" };
    case "failed": return { key: "failed", label: "Failed" };
    case "caveats": return { key: "warn", label: "Checked with caveats" };
    case "asked": return { key: "grey", label: "Asked a question back" };
    default:
      // Not "Verified": an answered run with no verdict we know is a run nobody vouched for.
      return { key: "warn", label: "Checked with caveats" };
  }
}


// Figures are matched by their digits so "$9,909,198", "9,909,198" and "9909198" agree.
export function figureKey(text) {
  return String(text || "").replace(/[^0-9.]/g, "").replace(/\.$/, "");
}


// ── the run's title ─────────────────────────────────────────────────────────
//
// The panel's header names THIS run: "Energy revenue and share in Central", never
// "The working — live" (reported on a review screenshot, 2026-09-10).
//
// THE TITLE IS WRITTEN BY THE MODEL, not by code here. It is the first argument of the
// run's own `write_plan` call and travels in the journal's header frame. An earlier
// version of this file derived a heading from the question with a list of regexes that
// stripped English question-openers — which only works in English, only for the phrasings
// someone thought of, and has to guess at casing. The model already knows what it is
// about to analyse, so it says so, and this reads it.
//
// Until the first plan lands (a second or two into a run) there is no title yet, and the
// question itself is the heading — the reader's own words, unedited.
export function titleOf(model) {
  const run = (model && model.run) || {};
  return String(run.title || "").trim() || String(run.question || "").trim();
}


// ── one decimal, everywhere the panel prints a figure ─────────────────────────
//
// The answer writes every figure at one decimal (the house style, enforced by the backend
// gate); the panel's cells arrived at two (36.49%, -1.48 pp, 70.81M), so the executive
// summary and the detailed working disagreed on the same number (QA, 2026-09-14). Rounding
// here, at display time, needs no backend change and no worker restart; the exact value is
// still on hover. Below 0.1 a figure keeps the digits that show its first non-zero digit,
// as the house style says, so 0.04 does not become 0.0. Integers, years and ids are never
// touched: only tokens that already carry two or more decimals are rounded.
const FIGURE_TOKEN = /(^|[^\w.])([-−+]?)(\d{1,3}(?:,\d{3})+|\d+)\.(\d+)(?=(\s?(?:%|pp|points?|[KMBk]|bn|x))?(?![\w.]))/g;

// The client's precision rides in the journal header (`run.decimals`, from the client config's
// `presentation.decimals`); 1 when a header predates it.
export const DEFAULT_DECIMALS = 1;

export function roundFigures(text, places = DEFAULT_DECIMALS) {
  const source = String(text ?? "");
  const target = Math.max(0, Math.min(6, Number.isFinite(Number(places)) ? Number(places) : DEFAULT_DECIMALS));
  if (!/\d\.\d+/.test(source)) return source;
  return source.replace(FIGURE_TOKEN, (match, before, sign, whole, fraction) => {
    const value = parseFloat(whole.replace(/,/g, "") + "." + fraction);
    if (!Number.isFinite(value)) return match;
    let places = target;
    const floor = Math.pow(10, -target);
    if (value !== 0 && value < floor) places = Math.max(target, -Math.floor(Math.log10(value)));
    if (places >= fraction.length) return match;
    // Half away from zero on the decimal string, not the binary float: 0.045.toFixed(2) is "0.04".
    const factor = Math.pow(10, places);
    const rounded = (Math.round((value + Number.EPSILON) * factor) / factor).toFixed(places);
    const [intPart, fracPart] = rounded.split(".");
    const grouped = whole.includes(",") ? Number(intPart).toLocaleString("en-US") : intPart;
    return `${before}${sign}${grouped}${fracPart !== undefined ? "." + fracPart : ""}`;
  });
}


// The name the first version shipped under.
export const oneDecimal = (text) => roundFigures(text, 1);


// ── a cell rounded from its exact value, never from its abbreviated text ───────
//
// The backend abbreviates a currency cell to four significant digits ("1.650M") and
// sends the unrounded value beside it (`exact`: "1,649,672.11"). Rounding the TEXT to one
// decimal rounded twice: 1.650 became 1.7M while the answer, rounding the value once, said
// 1.6M (QA, 2026-10-15). With an exact value the cell is rounded from that value, half
// away from zero, at the panel's decimals, and keeps its suffix. Without one, or without a
// suffix, the text is rounded as before.
const SUFFIX_SCALE = [["bn", 1e9], ["B", 1e9], ["MM", 1e6], ["mn", 1e6], ["M", 1e6], ["m", 1e6], ["K", 1e3], ["k", 1e3]];

export function roundCell(cell, places = DEFAULT_DECIMALS) {
  const text = String((cell && cell.t) ?? "");
  const exact = cell && cell.exact;
  if (!exact) return roundFigures(text, places);
  const value = Number(String(exact).split(",").join("").trim());
  if (!Number.isFinite(value)) return roundFigures(text, places);
  const trimmed = text.trim();
  const hit = SUFFIX_SCALE.find(([suffix]) => trimmed.endsWith(suffix));
  if (!hit) return roundFigures(text, places);
  const [suffix, scale] = hit;
  const target = Math.max(0, Math.min(6, Number.isFinite(Number(places)) ? Number(places) : DEFAULT_DECIMALS));
  const scaled = Math.abs(value) / scale;
  const factor = Math.pow(10, target);
  const magnitude = Math.round(scaled * factor + Number.EPSILON) / factor;
  const sign = value < 0 ? "-" : "";
  return `${sign}${magnitude.toFixed(target)}${suffix}`;
}
